/*
 * Mutations for the [energy_management_system_groups store]{link module:store/energy_management_system_groups}
 *
 * @module store/energy_management_system_groups/mutations
 */

/*
 * Mutation to reset the ems group store
 *
 * @function
 *
 */
export function RESET_EMS_GROUPS(state) {
  state.emsGroups.hash = {}
  state.emsGroups.hierarchy = {}
  state.emss = {}
  state.users = {}
}

/*
 * Mutation to reset the ems group store scores
 *
 * @function
 *
 */
export function RESET_EMS_GROUP_USER_MATCHING_SCORES(state) {
  for (const g of Object.values(state.emsGroups.hash)) g.userMatchingScore = 0
}

/*
 * Mutation to add/replace a single emsGroup {object} into the store
 *
 * Adds the emsGroup into `store.emsGroups.hash[emsGroup.id]``
 * Will replace the emsGroup information in case the ID is already existing
 *
 * @function
 *
 * @param {object} emsGroup has to be a [EmsGroup]{@link store/energy_management_system_groups.EmsGroup}
 *
 */
export function SET_EMS_GROUP(state, emsGroup) {
  state.emsGroups.hash[emsGroup.id] = emsGroup
}

/*
 * Mutation to Set the score of a single emsGroup {object} into the store
 *
 * @function
 *
 * @param {object} emsGroup has to be a [EmsGroup]{@link store/energy_management_system_groups.EmsGroup}
 *
 */
export function SET_EMS_GROUP_USER_MATCHING_SCORE(state, { groupId, score }) {
  if (!state.emsGroups.hash[groupId]) return

  state.emsGroups.hash[groupId].userMatchingScore = score
}

/*
 * Mutation to add/replace the emsGroups {object} into the store
 *
 * Adds the emsGroups relation into store.emsGroups.hash
 *
 * Will replace any emsGroup information
 *
 * @function
 *
 * @param {object} [id] -> {emsGroup}
 *
 */
export function SET_EMS_GROUPS(state, emsGroups) {
  for (var groupId of Object.keys(emsGroups)) {
    state.emsGroups.hash[groupId] = emsGroups[groupId]
  }
}

/*
 * Mutation to add/replace the emsGroup relation {object} into the store
 *
 * Adds the emsGroup relation into store.emsGroups.hierarchy
 *
 * Will replace the hierarchy information
 *
 * @function
 *
 * @param {object} [id]->{emsGroupIds}
 *
 */
export function SET_EMS_GROUPS_HIERARCHY(state, emsGroups) {
  state.emsGroups.hierarchy = emsGroups
}

/*
 * Mutation to add the emsGroupIds Array[Integer] for one EMS into the store.
 * Will remove duplicates.
 *
 * Adds the emsGroupsIds related to the EMS with ID emsID into store.emss[emsId]
 *
 * @function
 *
 * @param [Integer] emsId
 *
 * @param Array[Integer] emsGroupIds
 *
 */
export function ADD_GROUPS_FOR_EMS(state, { emsId, emsGroupIds }) {
  state.emss[emsId] = emsGroupIds.concat(state.emss[emsId]).filter((gId, i, gIds) => {
    return gId && gIds.indexOf(gId) === i
  })
}

/**
 * Performs a bluk (mass) assignment of the same group IDs to multiple users.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.userIds has to be a list of User IDs
 * @param {array} params.groupIds has to be the list of group IDs each user will be assigned.
 *
 */
export function BULK_SET_USERS(state, { userIds, groupIds }) {
  state.users = {}
  userIds.forEach((userId) => {
    state.users[userId] = groupIds
  })
}

/**
 * Adds a Group into the hierarchy under its parent
 *
 * @function
 *
 * @param {object} params
 * @param {integer} params.parentId the groups parent ID
 * @param {integer} params.groupId
 *
 */
export function ADD_GROUP_TO_HIERARCHY(state, { parentId, groupId }) {
  const parentGroup = findGroup(state.emsGroups.hierarchy, parentId)
  parentGroup[groupId] = {}
}

const findGroup = (tree, id) => {
  for (const childId in tree) {
    if (parseInt(childId) === id) {
      return tree[childId]
    } else {
      const res = findGroup(tree[childId], id)
      if (res) {
        return res
      }
    }
  }
}

/**
 * Removes a Group from the hash as well as hierarchy
 *
 * @function
 *
 * @param {object} params
 * @param {integer} params.id the group Id
 *
 */
export function REMOVE_GROUP(state, { id }) {
  if (state.emsGroups.hash[id]) {
    if (state.emsGroups.hash[id].parentId) {
      const group = findGroup(state.emsGroups.hierarchy, state.emsGroups.hash[id].parentId)
      delete group[id]
    }
    delete state.emsGroups.hash[id]
  }
}
